import React, { useEffect, useState } from 'react';
import { Pressable, View } from 'react-native';
import { newId, useAppStore } from '@restorant/core';
import type { MenuItem, PriceMap } from '@restorant/core';
import { radius, useColors } from '../theme';
import { useT } from '../i18n';
import { useCategories, useCategoryLabel } from '../menu';
import { Icon } from '../icons/Icon';
import type { IconName } from '../icons/Icon';
import { Chip } from './Chip';
import { Sheet, SheetButton, Toggle } from './Sheet';
import { T } from './Text';
import { TextField } from './TextField';

interface VariantRow {
  label: string;
  price: string;
}

function toRows(prices: PriceMap | undefined): VariantRow[] {
  const entries = Object.entries(prices ?? {});
  return entries.length
    ? entries.map(([label, price]) => ({ label, price: String(price) }))
    : [{ label: 'Price', price: '' }];
}

export function ItemEditor({
  item,
  defaultCategory,
  visible,
  onClose,
}: {
  /** null = creating a new one. */
  item: MenuItem | null;
  defaultCategory?: string;
  visible: boolean;
  onClose: () => void;
}) {
  const colors = useColors();
  const t = useT();
  const categories = useCategories(true);
  const catLabel = useCategoryLabel();
  const menuItems = useAppStore((s) => s.menuItems);
  const upsertMenuItem = useAppStore((s) => s.upsertMenuItem);
  const deleteMenuItem = useAppStore((s) => s.deleteMenuItem);
  const showToast = useAppStore((s) => s.showToast);

  const [name, setName] = useState('');
  const [nameHi, setNameHi] = useState('');
  const [nameGu, setNameGu] = useState('');
  const [cat, setCat] = useState(defaultCategory ?? categories[0]?.id ?? '');
  const [rows, setRows] = useState<VariantRow[]>([{ label: 'Price', price: '' }]);
  const [active, setActive] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    setName(item?.name ?? '');
    setNameHi(item?.nameHi ?? '');
    setNameGu(item?.nameGu ?? '');
    setCat(item?.cat ?? defaultCategory ?? categories[0]?.id ?? '');
    setRows(toRows(item?.prices));
    setActive(item?.active ?? true);
    setError(null);
    setConfirmDelete(false);
  }, [item, defaultCategory, visible]);

  const setRow = (i: number, patch: Partial<VariantRow>) =>
    setRows((rs) => rs.map((r, n) => (n === i ? { ...r, ...patch } : r)));

  const save = () => {
    if (!name.trim()) return setError(t('name_required'));
    if (!cat) return setError(t('name_required'));

    const prices: PriceMap = {};
    for (const r of rows) {
      const label = r.label.trim() || 'Price';
      const value = parseFloat(r.price);
      if (Number.isFinite(value) && value >= 0) prices[label] = value;
    }
    if (Object.keys(prices).length === 0) return setError(t('price_required'));

    const id = item?.id ?? 'm-' + newId().slice(0, 12);
    const siblings = menuItems.filter((i) => i.cat === cat && i.id !== id);

    upsertMenuItem({
      id,
      cat,
      name: name.trim(),
      nameHi: nameHi.trim(),
      nameGu: nameGu.trim(),
      prices,
      sortOrder: item?.sortOrder ?? (siblings.length + 1) * 10,
      active,
    });
    showToast(t('saved_toast'));
    onClose();
  };

  const remove = () => {
    if (!item) return;
    if (!confirmDelete) return setConfirmDelete(true);
    deleteMenuItem(item.id);
    showToast(t('deleted_toast'));
    onClose();
  };

  return (
    <Sheet
      visible={visible}
      title={item ? t('edit_item') : t('add_item')}
      onClose={onClose}
      footer={
        <>
          {item ? (
            <SheetButton
              label={confirmDelete ? t('delete_item_confirm') : t('delete')}
              icon="trash"
              tone="danger"
              onPress={remove}
            />
          ) : null}
          <SheetButton label={t('save')} icon="check" tone="primary" onPress={save} />
        </>
      }
    >
      <TextField testID="item-name" label={t('item_name')} value={name} onChangeText={setName} />
      <TextField
        testID="item-name-hi"
        label={t('name_hi')}
        value={nameHi}
        onChangeText={setNameHi}
        placeholder={name}
      />
      <TextField
        testID="item-name-gu"
        label={t('name_gu')}
        value={nameGu}
        onChangeText={setNameGu}
        placeholder={name}
      />

      <View style={{ gap: 8 }}>
        <T size={11.5} weight={600} color={colors.textFaint}>
          {t('category_name')}
        </T>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
          {categories.map((c) => (
            <Chip
              key={c.id}
              label={catLabel(c.id)}
              icon={c.icon as IconName}
              active={cat === c.id}
              onPress={() => setCat(c.id)}
            />
          ))}
        </View>
      </View>

      <View style={{ gap: 8 }}>
        <T size={11.5} weight={600} color={colors.textFaint}>
          {t('prices_label')}
        </T>
        <T size={11.5} color={colors.textFaint}>
          {t('variant_hint')}
        </T>

        {rows.map((r, i) => (
          // The only two-field row in the app that does not wrap. At the
          // default 160 floor the pair needed 328px and overflowed a 307px
          // sheet on a phone, clipping the price box off the right edge. A
          // price is three or four digits, so it does not need 160 — and
          // flexWrap is the backstop at the largest text size.
          <View
            key={i}
            style={{
              flexDirection: 'row',
              flexWrap: 'wrap',
              alignItems: 'flex-end',
              gap: 8,
            }}
          >
            <TextField
              testID={`variant-label-${i}`}
              label={t('variant_label')}
              value={r.label}
              onChangeText={(v) => setRow(i, { label: v })}
              placeholder="Price"
              minWidth={120}
            />
            <TextField
              testID={`variant-price-${i}`}
              label={t('price_label')}
              value={r.price}
              onChangeText={(v) => setRow(i, { price: v.replace(/[^0-9.]/g, '') })}
              keyboardType="number-pad"
              placeholder="0"
              minWidth={84}
            />
            {rows.length > 1 ? (
              <Pressable
                onPress={() => setRows((rs) => rs.filter((_, n) => n !== i))}
                style={{
                  padding: 12,
                  borderRadius: radius.md,
                  borderWidth: 1,
                  borderColor: colors.line,
                }}
              >
                <Icon name="trash" size={15} color={colors.spice} />
              </Pressable>
            ) : null}
          </View>
        ))}

        <Pressable
          onPress={() => setRows((rs) => [...rs, { label: '', price: '' }])}
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            paddingVertical: 11,
            borderRadius: radius.md,
            borderWidth: 1,
            borderStyle: 'dashed',
            borderColor: colors.line,
          }}
        >
          <Icon name="plus" size={15} color={colors.textDim} />
          <T size={12.5} weight={700} color={colors.textDim}>
            {t('add_variant')}
          </T>
        </Pressable>
      </View>

      <Toggle label={t('active_label')} value={active} onChange={setActive} />

      {error ? (
        <T size={12} color={colors.spice}>
          {error}
        </T>
      ) : null}
    </Sheet>
  );
}
